// src/components/admin/BookingRequestsTile.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, User, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { getFirestore, collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { useTenant } from '../../context/TenantContext';
import { safeLog } from '../../utils/gdprUtils';

const BookingRequestsTile = () => {
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  const [bookingRequests, setBookingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    declined: 0,
    total: 0
  });

  useEffect(() => {
    if (!currentTenant?.id) return;

    console.log('🔍 BookingRequestsTile: Loading requests for tenant:', currentTenant.id);
    
    const db = getFirestore();
    
    // Subscribe to booking requests
    const requestsQuery = query(
      collection(db, 'tenants', currentTenant.id, 'appointmentRequests'),
      orderBy('createdAt', 'desc'),
      limit(10)
    );

    const unsubscribe = onSnapshot(requestsQuery, (snapshot) => {
      const requests = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setBookingRequests(requests);

      // Calculate stats
      const newStats = {
        pending: requests.filter(r => r.status === 'pending').length,
        approved: requests.filter(r => r.status === 'approved').length,
        declined: requests.filter(r => r.status === 'declined').length,
        total: requests.length
      };
      setStats(newStats);
      setLoading(false);

      console.log('✅ BookingRequestsTile: Loaded', requests.length, 'booking requests');
      console.log('📊 BookingRequestsTile: Stats:', newStats);
      
      safeLog('Booking requests loaded', {
        tenantId: currentTenant.id,
        count: requests.length,
        pending: newStats.pending
      });
    }, (error) => {
      console.error('Error loading booking requests:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentTenant?.id]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'approved':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'declined':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="card-modern p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-200 rounded"></div>
            <div className="h-3 bg-gray-200 rounded w-5/6"></div>
            <div className="h-3 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-modern p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-bold text-gray-900 flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-blue-600" />
          Booking Requests
        </h3>
        <button
          onClick={() => navigate('/admin?tab=booking-requests')}
          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
        >
          View All
        </button>
      </div>

      {/* Stats Summary - Compact */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="text-center">
          <div className="text-lg font-bold text-yellow-600">
            {stats.pending}
          </div>
          <p className="text-xs text-gray-600">Pending</p>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-green-600">
            {stats.approved}
          </div>
          <p className="text-xs text-gray-600">Approved</p>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-red-600">
            {stats.declined}
          </div>
          <p className="text-xs text-gray-600">Declined</p>
        </div>
      </div>

      {/* Recent Requests - Compact */}
      <div className="space-y-2">
        {bookingRequests.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            <Calendar className="w-6 h-6 mx-auto mb-2 text-gray-300" />
            <p className="text-xs">No requests yet</p>
          </div>
        ) : (
          bookingRequests.slice(0, 2).map((request) => (
            <div
              key={request.id}
              className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer transition-colors"
              onClick={() => navigate('/admin?tab=booking-requests')}
            >
              <div className="flex items-center space-x-2">
                {getStatusIcon(request.status)}
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {request.clientDetails?.firstName} {request.clientDetails?.lastName}
                  </p>
                  <p className="text-xs text-gray-600">
                    {new Date(request.requestedSlot?.start).toLocaleDateString('en-GB')}
                  </p>
                </div>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                {request.status}
              </span>
            </div>
          ))
        )}
      </div>

      {bookingRequests.length > 2 && (
        <div className="mt-3 text-center">
          <button
            onClick={() => navigate('/admin?tab=booking-requests')}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            View {bookingRequests.length - 2} more
          </button>
        </div>
      )}
    </div>
  );
};

export default BookingRequestsTile;
